'use client';

import {
  MIN_PRICES,
  PACKAGE_STORAGE,
  SELECTED_FLIGHTS_STORAGE,
  SELECTED_HOTEL_STORAGE,
  SELECTED_TICKET_STORAGE,
} from '@config/storageKeys';
import { useCurrencyContext } from '@contexts/currency-context';
import { usePackagesParams } from '@hooks/use-packages-params';
import useStorage from '@hooks/useStorage';
import { IFormValues } from '@interfaces/buildPackage';
import { ISelectedHotel } from '@interfaces/toBeRefactored';
import { useBuildPackagesStore } from '@store/build-package-store';
import { useCheckoutStepsStore } from '@store/checkout-steps-store';
import { useTicketsStore } from '@store/tickets-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { ChevronDown } from '@v2/ui/Icon/Arrow';
import { PreBookingPayload } from '@v2/views/flights/components/FlightsContent/FlightDrawer/FlightFares';
import {
  CheckoutHeaderQuote,
  resolveCheckoutHeaderQuote,
} from './checkout-header-quote';
import { formatTravelerLabel } from './header-display';
import { buildSearchHeaderSummary } from './package-header-summary';

function readJson<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function PackageSummary({
  className = '',
  showWhenEmpty = false,
  showChevron = true,
  quote,
}: {
  className?: string;
  showWhenEmpty?: boolean;
  showChevron?: boolean;
  quote?: CheckoutHeaderQuote | null;
}) {
  const storage = useStorage();
  const { currency, priceRate } = useCurrencyContext();
  const { packageType } = usePackagesParams();
  const selectedTicketPrice = useBuildPackagesStore(
    (state) => state.selectedTicketPrice
  );
  const ticketQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const flightsSelected = useCheckoutStepsStore(
    (state) => state.flightsSelected
  );
  const hotelSelected = useCheckoutStepsStore((state) => state.hotelSelected);
  const ticketSelected = useCheckoutStepsStore((state) => state.ticketSelected);

  const storedPackage = readJson<IFormValues>(
    storage.getItem(PACKAGE_STORAGE),
    {
      tickets: 0,
      hotelStartDate: '',
      hotelEndDate: '',
      roomConfigurations: [],
    }
  );
  const minPrices = readJson<{ ticket?: number; hotel?: number }>(
    storage.getItem(MIN_PRICES),
    {}
  );
  const selectedHotel = readJson<Partial<ISelectedHotel>>(
    storage.getItem(SELECTED_HOTEL_STORAGE),
    {}
  );
  const selectedFlights = readJson<PreBookingPayload | null>(
    storage.getItem(SELECTED_FLIGHTS_STORAGE),
    null
  );
  const selectedTicket = readJson<{ quantity?: number } | null>(
    storage.getItem(SELECTED_TICKET_STORAGE),
    null
  );

  const isTicketsOnly = packageType === 'tickets';
  const includeTickets =
    isTicketsOnly ||
    packageType === 'packages' ||
    ticketSelected ||
    Boolean(storedPackage?.selected?.tickets);
  const includeHotel =
    !isTicketsOnly &&
    (packageType === 'packages' ||
      hotelSelected ||
      Boolean(storedPackage?.selected?.hotel));
  const includeFlights =
    !isTicketsOnly &&
    (flightsSelected || Boolean(storedPackage?.selected?.flights));

  const roomSelections = selectedHotel.roomSelections ?? [];
  const summary = buildSearchHeaderSummary({
    includeTickets,
    includeHotel,
    includeFlights,
    selectedTicketPrice,
    minTicketPrice: minPrices.ticket || 0,
    roomPrices: roomSelections.map((room) => room.price),
    hasRoomSelections: roomSelections.length > 0,
    minHotelPrice: minPrices.hotel || 0,
    flightPackagePrice: selectedFlights?.simpleFare?.packagePrice
      ? Number(selectedFlights.simpleFare.packagePrice)
      : null,
    packageData: storedPackage,
    flightData: selectedFlights,
    ticketData: selectedTicket ?? { quantity: ticketQuantity },
  });

  const resolvedQuote = resolveCheckoutHeaderQuote(quote);
  if (resolvedQuote.source === 'pending') {
    return (
      <p
        className={`text-xs leading-[18px] text-gray-500 ${className}`}
        aria-busy="true"
      >
        Updating price
      </p>
    );
  }

  if (resolvedQuote.source === 'quote') {
    return (
      <PriceSummary
        className={className}
        showChevron={showChevron}
        perPerson={resolvedQuote.display.perPerson}
        total={resolvedQuote.display.total}
        travelers={resolvedQuote.display.travelers}
      />
    );
  }

  if (summary.total <= 0) {
    if (!showWhenEmpty) return null;
    return (
      <p className={`text-xs leading-[18px] text-gray-500 ${className}`}>
        Nothing added yet
      </p>
    );
  }

  const perPerson = formatCurrencyV2(
    summary.perPerson,
    priceRate,
    currency,
    true,
    false
  );
  const total = formatCurrencyV2(
    summary.total,
    priceRate,
    currency,
    true,
    false
  );

  return (
    <PriceSummary
      className={className}
      showChevron={showChevron}
      perPerson={perPerson}
      total={total}
      travelers={summary.travelers}
    />
  );
}

function PriceSummary({
  className,
  showChevron,
  perPerson,
  total,
  travelers,
}: {
  className: string;
  showChevron: boolean;
  perPerson: string;
  total: string;
  travelers: number;
}) {
  return (
    <div
      className={`flex flex-col items-end rounded-lg bg-white px-2 py-1 text-right ${className}`}
    >
      <div className="flex items-center gap-1">
        <p className="flex items-baseline gap-1 whitespace-nowrap">
          <span className="text-base font-bold leading-6 text-gray-900">
            {perPerson}
          </span>
          <span className="text-xs font-normal leading-[18px] text-gray-500">
            / person
          </span>
        </p>
        {showChevron ? (
          <ChevronDown size="20" className="text-gray-500" />
        ) : null}
      </div>
      <p className="whitespace-nowrap text-xs leading-[18px] text-gray-500">
        {total} total · {formatTravelerLabel(travelers)}
      </p>
    </div>
  );
}
